import { zodResolver } from "@hookform/resolvers/zod";
import {
  createWorkspaceSchema,
  type CreateWorkspaceInput,
  type WorkspaceDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth-layout";
import { FormField } from "@/components/form-field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";

export const Route = createFileRoute("/app/novo-workspace")({
  component: NovoWorkspacePage,
});

function NovoWorkspacePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspaces = useQuery(queries.workspaces());
  const form = useForm<CreateWorkspaceInput>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: { name: "" },
  });

  const criar = useMutation({
    mutationFn: (input: CreateWorkspaceInput) =>
      api<WorkspaceDto>("/api/workspaces", { method: "POST", body: input }),
    onSuccess: async (ws) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      toast.success("Workspace criado.");
      await navigate({ to: "/app/$workspaceId", params: { workspaceId: ws.id } });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const temOutros = (workspaces.data?.length ?? 0) > 0;

  return (
    <AuthLayout
      title={temOutros ? "Novo workspace" : "Crie seu workspace"}
      description="O workspace é a sua agência ou operação. Os clientes, contas Meta e membros ficam dentro dele."
    >
      <form onSubmit={form.handleSubmit((v) => criar.mutate(v))} className="space-y-4" noValidate>
        <FormField
          id="name"
          label="Nome do workspace"
          error={form.formState.errors.name?.message}
          hint="Ex.: Agência Norte, Tráfego da Ana."
        >
          <Input id="name" autoFocus placeholder="Minha agência" {...form.register("name")} />
        </FormField>
        <Button type="submit" className="w-full" loading={criar.isPending}>
          Criar workspace
        </Button>
        {temOutros ? (
          <Button asChild variant="ghost" className="w-full">
            <Link to="/app">Voltar</Link>
          </Button>
        ) : null}
      </form>
    </AuthLayout>
  );
}
